import { useMemo, useRef, useState } from "react";

import { SuccessToast, useSuccessFeedback } from "./useSuccessFeedback";
import CameraIcon from "./common/CameraIcon";
import EditIcon from "./common/EditIcon";
import ListTextPreview from "./common/ListTextPreview";
import TrashIcon from "./common/TrashIcon";
import { createAttachmentName } from "../utils/attachmentNaming";
import { processImageFile } from "../utils/imageProcessing";

const checklistQuestions = [
  "Ist die Ware im Zertifizierungsbereich des Kunden?",
  "Lieferschein enthaelt Kennzeichnung, dass es sich um BIO-Ware handelt",
  "Lieferschein enthaelt Codenummer der Kontrollstelle",
  "Lieferschein enthaelt Menge und Gewicht der gelieferten Artikel",
  "Die gelieferten Mengen und Gewichte stimmen mit dem Lieferschein ueberein",
  "Die Ware ist vollstaendig unversehrt",
  "Die Artikeletiketten enthalten die Bio-Kennzeichnung und die Codenummer der Kontrollstelle",
];

const conditions = ["Gut", "Leicht beschaedigt", "Beschaedigt", "Gesperrt"];
const palletTypes = [
  "Euro Palette",
  "Einweg Palette",
  "Gitterbox",
  "Kundenpalette",
  "Sonstige",
];

const initialForm = {
  selectedSupplier: "",
  packageCount: 0,
  packageCondition: "Gut",
  palletCount: 0,
  palletType: "",
  palletCondition: "Gut",
  comment: "",
  photoData: "",
  photoDataList: [],
  deliveryNoteData: "",
  deliveryNoteDataList: [],
};

function clampNumber(value) {
  return Math.max(0, Number(value) || 0);
}

function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(seconds / 60);
  const restSeconds = seconds % 60;

  return `${minutes}m ${String(restSeconds).padStart(2, "0")}s`;
}

function isBioSupplier(supplier) {
  const bioMode = supplier?.bioMode || (supplier?.bio ? "bio" : "nein");
  return bioMode === "bio" || bioMode === "bio_und_nicht_bio";
}

function downloadStoredImage(dataUrl, fileName) {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function getStoredImages(record, listFieldName, singleFieldName) {
  const images = Array.isArray(record?.[listFieldName])
    ? record[listFieldName].filter(Boolean)
    : [];
  const legacyImage = record?.[singleFieldName];

  if (!legacyImage || images.includes(legacyImage)) {
    return images;
  }

  return [legacyImage, ...images];
}

function getBioImageDownloadName(record, attachmentType, dataUrl, index) {
  return createAttachmentName({
    area: "BioWareneingang",
    processNumber: record.processNumber,
    attachmentType,
    sequence: index + 1,
    dataUrl,
  });
}

function getDeviationSequence(record, questionIndex, photoIndex) {
  const previousCount = (record.checklistData || [])
    .slice(0, questionIndex)
    .reduce(
      (total, item) =>
        total + getStoredImages(item, "photoDataList", "photoData").length,
      0
    );
  return previousCount + photoIndex;
}

function downloadCsv(fileName, rows) {
  const csvContent = rows
    .map((row) =>
      row
        .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
        .join(";")
    )
    .join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function openStoredImage(dataUrl) {
  const imageWindow = window.open();

  if (!imageWindow) {
    window.alert("Bitte erlaube Pop-ups, damit das Bild geoeffnet werden kann.");
    return;
  }

  imageWindow.document.write(
    `<img src="${dataUrl}" alt="Gespeichertes Bild" style="max-width:100%;height:auto;display:block;margin:0 auto;" />`
  );
  imageWindow.document.close();
}

export default function BioWareneingang({
  records,
  suppliers,
  user,
  onSaveRecord,
  onDeleteRecord,
  onToggleProcessed,
}) {
  const [form, setForm] = useState(initialForm);
  const [checklist, setChecklist] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [deviationReason, setDeviationReason] = useState("");
  const [deviationPhotos, setDeviationPhotos] = useState([]);
  const [recordsVisible, setRecordsVisible] = useState(false);
  const [processStartedAt, setProcessStartedAt] = useState(null);
  const deviationPhotoInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const deliveryNoteInputRef = useRef(null);
  const { successVisible, triggerSuccessFeedback } = useSuccessFeedback();
  const hasPallets = Number(form.palletCount) > 0;

  const bioSuppliers = useMemo(() => {
    return suppliers.filter(isBioSupplier);
  }, [suppliers]);
  const suppliersByName = useMemo(() => {
    return new Map(suppliers.map((supplier) => [supplier.name, supplier]));
  }, [suppliers]);

  const allQuestionsAnswered =
    form.selectedSupplier && checklist.length === checklistQuestions.length;
  const hasRejectedQuestion = checklist.some((item) => item.answer === "nein");

  const handleSupplierChange = (supplierName) => {
    setForm({ ...initialForm, selectedSupplier: supplierName });
    setChecklist([]);
    setCurrentQuestionIndex(0);
    setDeviationReason("");
    setDeviationPhotos([]);
    setProcessStartedAt(supplierName ? Date.now() : null);
  };

  const handleQuestionAnswer = (answer) => {
    const question = checklistQuestions[currentQuestionIndex];
    const nextChecklist = checklist.filter((item) => item.question !== question);

    if (answer === "ja") {
      nextChecklist.push({
        question,
        answer: "ja",
        reason: "",
        photoData: "",
        photoDataList: [],
      });
      setChecklist(nextChecklist);
      setDeviationReason("");
      setDeviationPhotos([]);
      setCurrentQuestionIndex((current) =>
        Math.min(current + 1, checklistQuestions.length - 1)
      );
      return;
    }

    nextChecklist.push({
      question,
      answer: "nein",
      reason: deviationReason.trim(),
      photoData: deviationPhotos[0] || "",
      photoDataList: deviationPhotos,
    });
    setChecklist(nextChecklist);
    setDeviationReason("");
    setDeviationPhotos([]);
    setCurrentQuestionIndex((current) =>
      Math.min(current + 1, checklistQuestions.length - 1)
    );
  };

  const handleImageCapture = async (event, fieldName) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) {
      return;
    }

    try {
      const dataUrls = await Promise.all(files.map(processImageFile));

      if (fieldName === "deviationPhoto") {
        setDeviationPhotos((currentPhotos) => [...currentPhotos, ...dataUrls]);
      } else {
        setForm((currentForm) => ({
          ...currentForm,
          [fieldName]: currentForm[fieldName] || dataUrls[0] || "",
          [`${fieldName}List`]: [
            ...(Array.isArray(currentForm[`${fieldName}List`])
              ? currentForm[`${fieldName}List`]
              : currentForm[fieldName]
                ? [currentForm[fieldName]]
                : []),
            ...dataUrls,
          ],
        }));
      }
    } catch (error) {
      window.alert(error.message);
    } finally {
      event.target.value = "";
    }
  };

  const handleSave = async () => {
    if (!form.selectedSupplier) {
      window.alert("Bitte zuerst einen BIO Kunden auswaehlen.");
      return;
    }

    if (!allQuestionsAnswered) {
      window.alert("Bitte zuerst alle BIO Fragen beantworten.");
      return;
    }

    if (!hasRejectedQuestion && hasPallets && !form.palletType) {
      window.alert("Bitte waehle den Palettentyp aus.");
      return;
    }

    try {
      await onSaveRecord({
        timestamp: new Date().toLocaleString("de-DE"),
        selectedSupplier: form.selectedSupplier,
        checklistData: checklist,
        packageCount: hasRejectedQuestion ? 0 : clampNumber(form.packageCount),
        packageCondition:
          !hasRejectedQuestion && clampNumber(form.packageCount) > 0
            ? form.packageCondition
            : "-",
        palletCount: hasRejectedQuestion ? 0 : clampNumber(form.palletCount),
        palletType: !hasRejectedQuestion && hasPallets ? form.palletType : "-",
        palletCondition:
          !hasRejectedQuestion && hasPallets ? form.palletCondition : "-",
        comment: form.comment.trim(),
        photoData: hasRejectedQuestion ? "" : form.photoData,
        photoDataList: hasRejectedQuestion ? [] : form.photoDataList,
        deliveryNoteData: hasRejectedQuestion ? "" : form.deliveryNoteData,
        deliveryNoteDataList: hasRejectedQuestion
          ? []
          : form.deliveryNoteDataList,
        processDurationSeconds: processStartedAt
          ? Math.max(0, Math.round((Date.now() - processStartedAt) / 1000))
          : 0,
      });
      setForm(initialForm);
      setChecklist([]);
      setCurrentQuestionIndex(0);
      setProcessStartedAt(null);
      triggerSuccessFeedback();
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleCsvDownload = () => {
    if (records.length === 0) {
      window.alert("Es gibt keine BIO WE Daten fuer den CSV-Export.");
      return;
    }

    downloadCsv("bio-wareneingang.csv", [
      [
        "Zeit",
        "Kunde",
        "Kundennummer",
        "Status",
        "Pakete",
        "Zustand Pakete",
        "Paletten",
        "Palettentyp",
        "Zustand Paletten",
        "Kommentar",
        "Bearbeitungszeit",
        "Bearbeitet",
        "Pruefung",
      ],
      ...[...records].reverse().map((record) => {
        const blocked = (record.checklistData || []).some(
          (item) => item.answer === "nein"
        );
        const supplier = suppliersByName.get(record.selectedSupplier);

        return [
          record.timestamp,
          record.selectedSupplier,
          supplier?.customerNumber || "",
          blocked ? "Gesperrt" : "Freigegeben",
          record.packageCount,
          record.packageCondition,
          record.palletCount,
          record.palletType,
          record.palletCondition,
          record.comment,
          formatDuration(record.processDurationSeconds),
          record.processed ? "Ja" : "Nein",
          (record.checklistData || [])
            .map(
              (item) =>
                `${item.answer === "ja" ? "Ja" : "Nein"}: ${item.question}${
                  item.reason ? ` (${item.reason})` : ""
                }`
            )
            .join(" | "),
        ];
      }),
    ]);
  };

  const currentQuestion = checklistQuestions[currentQuestionIndex];

  const handleToggleProcessed = async (record) => {
    try {
      await onToggleProcessed(record.id, !record.processed);
    } catch (error) {
      window.alert(error.message);
    }
  };

  return (
    <div className="page-stack">
      <SuccessToast visible={successVisible} />
      <div>
        <p className="eyebrow">Erfassung</p>
        <h1 className="page-title">BIO Wareneingang</h1>
        <p className="section-text">
          BIO Kunde waehlen, Prueffragen beantworten und danach den Wareneingang speichern.
        </p>
      </div>

      <section className="card">
        <div className="section-head">
          <div>
            <h2>BIO Kunde auswaehlen</h2>
            <p className="section-text">
              Angezeigt werden Kunden mit Bio oder Bio und nicht bio.
            </p>
          </div>
          <div className="entry-summary">
            <span>{form.selectedSupplier || "Kein Kunde"}</span>
            <span>
              {checklist.length} / {checklistQuestions.length} Fragen
            </span>
          </div>
        </div>

        <label className="field">
          <span>BIO Kunde</span>
          <select
            value={form.selectedSupplier}
            onChange={(event) => handleSupplierChange(event.target.value)}
          >
            <option value="">Bitte auswaehlen</option>
            {bioSuppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.name}>
                {supplier.customerNumber
                  ? `${supplier.short} - ${supplier.customerNumber}`
                  : supplier.short}
              </option>
            ))}
          </select>
        </label>
      </section>

      {form.selectedSupplier && !allQuestionsAnswered && (
        <section className="card bio-question-card">
          <div className="section-head">
            <div>
              <h2>Prueffrage {currentQuestionIndex + 1}</h2>
              <p className="section-text">{currentQuestion}</p>
            </div>
          </div>

          <div className="quick-toggle-row">
            <button
              type="button"
              onClick={() => handleQuestionAnswer("ja")}
              className="button button-primary"
            >
              Ja
            </button>
            <button
              type="button"
              onClick={() => handleQuestionAnswer("nein")}
              className="button button-danger"
            >
              Nein
            </button>
          </div>

          <div className="form-grid columns-2">
            <label className="field field-wide">
              <span>Grund bei Nein</span>
              <textarea
                rows="3"
                value={deviationReason}
                onChange={(event) => setDeviationReason(event.target.value)}
                placeholder="Optionalen Grund erfassen"
              />
            </label>

            <div className="field field-wide">
              <span>Foto bei Nein</span>
              <div className="capture-card">
                <strong>
                  {deviationPhotos.length > 0
                    ? `${deviationPhotos.length} Foto(s) vorhanden`
                    : "Kein Foto aufgenommen"}
                </strong>
                <div className="inline-actions">
                  <input
                    ref={deviationPhotoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    capture="environment"
                    className="visually-hidden"
                    onChange={(event) =>
                      handleImageCapture(event, "deviationPhoto")
                    }
                  />
                  <button
                    type="button"
                    onClick={() => deviationPhotoInputRef.current?.click()}
                    className="button button-secondary"
                  >
                    Foto aufnehmen
                  </button>
                  {deviationPhotos.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setDeviationPhotos((currentPhotos) =>
                          currentPhotos.slice(0, -1)
                        )
                      }
                      className="button button-inline-danger"
                    >
                      Letztes entfernen
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {allQuestionsAnswered && hasRejectedQuestion && (
        <section className="card">
          <div className="section-head">
            <div>
              <h2>BIO Wareneingang gesperrt</h2>
              <p className="section-text">
                Mindestens eine Prueffrage wurde mit Nein beantwortet. Der
                Wareneingang darf nicht durchgefuehrt werden; die Pruefung wird
                trotzdem in der BIO-WE-Liste gespeichert.
              </p>
            </div>
          </div>

          <label className="field field-wide">
            <span>Kommentar zur gesperrten Pruefung</span>
            <textarea
              rows="3"
              value={form.comment}
              onChange={(event) =>
                setForm({ ...form, comment: event.target.value })
              }
            />
          </label>

          <div className="button-row action-bar-sticky">
            <button onClick={handleSave} className="button button-primary">
              BIO Pruefung speichern
            </button>
            <button
              onClick={() => {
                setForm(initialForm);
                setChecklist([]);
                setCurrentQuestionIndex(0);
              }}
              className="button button-secondary"
            >
              Formular zuruecksetzen
            </button>
          </div>
        </section>
      )}

      {allQuestionsAnswered && !hasRejectedQuestion && (
        <section className="card">
          <div className="section-head">
            <div>
              <h2>BIO Wareneingang speichern</h2>
              <p className="section-text">
                Alle Prueffragen sind beantwortet. Erfasse jetzt Mengen und Fotos.
              </p>
            </div>
          </div>

          <div className="form-grid columns-2 entry-form-grid">
            <label className="field">
              <span>Anzahl Pakete</span>
              <input
                type="number"
                min="0"
                value={form.packageCount}
                onChange={(event) =>
                  setForm({
                    ...form,
                    packageCount: clampNumber(event.target.value),
                  })
                }
              />
            </label>

            <label className="field">
              <span>Zustand Pakete</span>
              <select
                value={form.packageCondition}
                onChange={(event) =>
                  setForm({ ...form, packageCondition: event.target.value })
                }
              >
                {conditions.map((condition) => (
                  <option key={condition} value={condition}>
                    {condition}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Anzahl Paletten</span>
              <input
                type="number"
                min="0"
                value={form.palletCount}
                onChange={(event) =>
                  setForm({
                    ...form,
                    palletCount: clampNumber(event.target.value),
                  })
                }
              />
            </label>

            {hasPallets && (
              <>
                <label className="field">
                  <span>Palettentyp</span>
                  <select
                    value={form.palletType}
                    onChange={(event) =>
                      setForm({ ...form, palletType: event.target.value })
                    }
                  >
                    <option value="">Bitte auswaehlen</option>
                    {palletTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field">
                  <span>Zustand Paletten</span>
                  <select
                    value={form.palletCondition}
                    onChange={(event) =>
                      setForm({ ...form, palletCondition: event.target.value })
                    }
                  >
                    {conditions.map((condition) => (
                      <option key={condition} value={condition}>
                        {condition}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}

            <label className="field field-wide">
              <span>Kommentar</span>
              <textarea
                rows="3"
                value={form.comment}
                onChange={(event) =>
                  setForm({ ...form, comment: event.target.value })
                }
              />
            </label>

            <div className="field field-wide">
              <span>Foto zum Vorgang</span>
              <div className="capture-card">
                <strong>
                  {form.photoDataList.length > 0
                    ? `${form.photoDataList.length} Foto(s) vorhanden`
                    : "Kein Foto"}
                </strong>
                <div className="inline-actions">
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    capture="environment"
                    className="visually-hidden"
                    onChange={(event) => handleImageCapture(event, "photoData")}
                  />
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="button button-secondary"
                  >
                    Foto aufnehmen
                  </button>
                  {form.photoDataList.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setForm((currentForm) => {
                          const photoDataList = currentForm.photoDataList.slice(
                            0,
                            -1
                          );

                          return {
                            ...currentForm,
                            photoDataList,
                            photoData: photoDataList[0] || "",
                          };
                        })
                      }
                      className="button button-inline-danger"
                    >
                      Letztes entfernen
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="field field-wide">
              <span>Lieferschein</span>
              <div className="capture-card">
                <strong>
                  {form.deliveryNoteDataList.length > 0
                    ? `${form.deliveryNoteDataList.length} Lieferschein-Foto(s) vorhanden`
                    : "Kein Lieferschein"}
                </strong>
                <div className="inline-actions">
                  <input
                    ref={deliveryNoteInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    capture="environment"
                    className="visually-hidden"
                    onChange={(event) =>
                      handleImageCapture(event, "deliveryNoteData")
                    }
                  />
                  <button
                    type="button"
                    onClick={() => deliveryNoteInputRef.current?.click()}
                    className="button button-secondary"
                  >
                    LS fotografieren
                  </button>
                  {form.deliveryNoteDataList.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setForm((currentForm) => {
                          const deliveryNoteDataList =
                            currentForm.deliveryNoteDataList.slice(0, -1);

                          return {
                            ...currentForm,
                            deliveryNoteDataList,
                            deliveryNoteData: deliveryNoteDataList[0] || "",
                          };
                        })
                      }
                      className="button button-inline-danger"
                    >
                      Letztes entfernen
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="button-row action-bar-sticky">
            <button onClick={handleSave} className="button button-primary">
              BIO Wareneingang speichern
            </button>
            <button
              onClick={() => {
                setForm(initialForm);
                setChecklist([]);
                setCurrentQuestionIndex(0);
              }}
              className="button button-secondary"
            >
              Formular zuruecksetzen
            </button>
          </div>
        </section>
      )}

      <section className="card">
        <div className="section-head">
          <div>
            <h2>Gespeicherte BIO Wareneingaenge</h2>
            <p className="section-text">
              BIO Pruefung und Wareneingang werden gemeinsam gespeichert.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setRecordsVisible((current) => !current)}
            className="button button-dark mde-only-button"
          >
            {recordsVisible ? "Liste ausblenden" : "Liste anzeigen"}
          </button>
          {user?.isAdmin && (
            <button
              type="button"
              onClick={handleCsvDownload}
              className="button button-primary"
            >
              CSV herunterladen
            </button>
          )}
        </div>

        <div
          className={`mde-records-body ${
            recordsVisible ? "mde-records-expanded" : "mde-records-collapsed"
          }`}
        >
          {records.length === 0 ? (
            <div className="empty-state">Keine BIO Wareneingaenge vorhanden.</div>
          ) : (
            <div className="table-wrap">
              <table className="data-table bio-we-table">
                <thead>
                  <tr>
                    <th>Zeit</th>
                    <th>Kunde</th>
                    <th>Kundennummer</th>
                    <th>Pakete</th>
                    <th>Paletten</th>
                    <th>Status</th>
                    <th>Bearbeitungszeit</th>
                    <th>Pruefung</th>
                    <th>Foto</th>
                    <th>Lieferschein</th>
                    <th>Bearbeitet</th>
                    <th>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {[...records].reverse().map((record) => (
                    <tr
                      key={record.id}
                      className={record.processed ? "row-processed" : ""}
                    >
                      <td>{record.timestamp}</td>
                      <td>{record.selectedSupplier}</td>
                      <td>
                        {suppliersByName.get(record.selectedSupplier)?.customerNumber ||
                          "-"}
                      </td>
                      <td>{record.packageCount}</td>
                      <td>{record.palletCount}</td>
                      <td>
                        {(record.checklistData || []).some(
                          (item) => item.answer === "nein"
                        )
                          ? "Gesperrt"
                          : "Freigegeben"}
                      </td>
                      <td>{formatDuration(record.processDurationSeconds)}</td>
                      <td>
                        <details className="audit-details">
                          <summary>Antworten anzeigen</summary>
                          {(record.checklistData || []).map((item, index) => {
                            const deviationImages = getStoredImages(
                              item,
                              "photoDataList",
                              "photoData"
                            );

                            return (
                              <div
                                key={`${record.id}-${index}`}
                                className="bio-answer"
                              >
                                <strong>
                                  {item.answer === "ja" ? "Ja" : "Nein"}:
                                </strong>{" "}
                                <ListTextPreview value={item.question} />
                                {item.reason && <p><ListTextPreview value={item.reason} /></p>}
                                {deviationImages.map((imageData, photoIndex) => (
                                  <div
                                    key={`${record.id}-${index}-${photoIndex}`}
                                    className="media-actions"
                                  >
                                    <button
                                      type="button"
                                      onClick={() => openStoredImage(imageData)}
                                      className="button button-inline-edit button-image-open"
                                      aria-label={`Bild ${photoIndex + 1} ansehen`}
                                      title={`Bild ${photoIndex + 1}`}
                                    >
                                      <CameraIcon />
                                      <span>{photoIndex + 1}</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        downloadStoredImage(
                                          imageData,
                                          getBioImageDownloadName(
                                            record,
                                            "SCHADEN",
                                            imageData,
                                            getDeviationSequence(record, index, photoIndex)
                                          )
                                        )
                                      }
                                      className="button button-icon-download"
                                      aria-label={`Abweichungsfoto ${photoIndex + 1} herunterladen`}
                                    >
                                      ↓
                                    </button>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </details>
                      </td>
                      <td>
                        {getStoredImages(record, "photoDataList", "photoData")
                          .length > 0 ? (
                          getStoredImages(record, "photoDataList", "photoData").map(
                            (imageData, index) => (
                              <button
                                key={`${record.id}-photo-${index}`}
                                type="button"
                                onClick={() =>
                                  downloadStoredImage(
                                    imageData,
                                    getBioImageDownloadName(record, "BILD", imageData, index)
                                  )
                                }
                                className="button button-icon-download"
                                aria-label={`BIO Foto ${index + 1} herunterladen`}
                              >
                                ↓
                              </button>
                            )
                          )
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {getStoredImages(
                          record,
                          "deliveryNoteDataList",
                          "deliveryNoteData"
                        ).length > 0 ? (
                          getStoredImages(
                            record,
                            "deliveryNoteDataList",
                            "deliveryNoteData"
                          ).map((imageData, index) => (
                            <button
                              key={`${record.id}-delivery-note-${index}`}
                              type="button"
                              onClick={() =>
                                downloadStoredImage(
                                  imageData,
                                  getBioImageDownloadName(
                                    record,
                                    "LS",
                                    imageData,
                                    index
                                  )
                                )
                              }
                              className="button button-icon-download"
                              aria-label={`BIO Lieferschein ${index + 1} herunterladen`}
                            >
                              ↓
                            </button>
                          ))
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => handleToggleProcessed(record)}
                          aria-label="Bearbeitet umschalten"
                          title="Bearbeitet"
                          className={`button button-inline-processed ${
                            record.processed ? "button-inline-processed-active" : ""
                          }`}
                        >
                          <EditIcon />
                        </button>
                      </td>
                      <td>
                        {user?.isAdmin ? (
                          <button
                            onClick={() => onDeleteRecord(record.id)}
                            className="button button-inline-danger button-icon-danger"
                            aria-label="Loeschen"
                            title="Loeschen"
                          >
                            <TrashIcon />
                          </button>
                        ) : (
                          <span className="section-text">Keine Berechtigung</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
